/**
 * The conversion the operator is assembling, and what counts as done.
 *
 * Pure on purpose. Which steps are complete drives the progress indicator, which steps open by
 * default, and whether the convert button is live - so it is worth being able to test without
 * a browser, the way the rest of this workspace tests.
 *
 * The eight steps mirror the desktop application's, in the same order, because drafters are
 * moving between the two and a reordered workflow is a retraining cost for no benefit. Two of
 * its controls are deliberately absent: installing a local ONNX model and repairing the
 * AutoCAD connector are both properties of a machine, and this runs on a server.
 *
 * A project is pages, and a page has floors: `pages` holds each page's scale and floor count,
 * `areas` holds one polygon per (page, floor). That is the desktop application's model and the
 * package's - `PageConversionData` is one entry per page per floor.
 */

import {
  inchesPerPreviewPixel,
  parseArchitecturalDistanceInches,
  type Point,
  type Size,
} from "@plan-forge/conversion-core";
import type { OutputType } from "@plan-forge/job-contract";

export type StepId =
  | "output"
  | "source"
  | "details"
  | "preview"
  | "calibration"
  | "area"
  | "recognition"
  | "output-package";

export const STEPS: readonly { readonly id: StepId; readonly title: string }[] = [
  { id: "output", title: "Output type" },
  { id: "source", title: "Floor-plan file" },
  { id: "details", title: "School and district information" },
  { id: "preview", title: "Preview" },
  { id: "calibration", title: "Scale calibration" },
  { id: "area", title: "Conversion area" },
  { id: "recognition", title: "Recognition quality check" },
  { id: "output-package", title: "Create output" },
];

/** What the pointer does on the canvas. Only one thing at a time, as in the desktop app. */
export type CanvasMode = "pan" | "calibrate" | "draw";

/** The desktop application's ceiling on floors drawn on one page. */
export const MAX_FLOORS_PER_PAGE = 12;

export interface SchoolDetails {
  readonly schoolName: string;
  readonly schoolAddress: string;
  readonly districtName: string;
  readonly districtAddress: string;
  readonly state: string;
  readonly country: "United States" | "Canada";
  readonly portalId: string;
}

export const EMPTY_DETAILS: SchoolDetails = {
  schoolName: "",
  schoolAddress: "",
  districtName: "",
  districtAddress: "",
  state: "",
  country: "United States",
  portalId: "",
};

export interface Calibration {
  readonly from: Point | null;
  readonly to: Point | null;
  /** As typed. Parsed by conversion-core, which accepts 3', 3'-0", 36", 10'-6" and plain inches. */
  readonly distanceText: string;
}

export const DEFAULT_CALIBRATION: Calibration = {
  from: null,
  to: null,
  // The desktop application's default, and its advice: a 3'-0" door is the most reliably
  // known distance on a school floor plan.
  distanceText: `3'-0"`,
};

export type JobPhase = "none" | "submitting" | "queued" | "running" | "succeeded" | "failed";

/**
 * Where packaging is. Deliberately not folded into JobPhase: a package is created from a
 * finished job and can be created again, so "the job succeeded" and "a package exists" are
 * two facts and collapsing them would lose the ability to say the second one changed.
 */
export type PackagePhase = "none" | "creating" | "created" | "failed";

/**
 * A finished artifact of step 8, whichever output was chosen.
 *
 * `x1` is the project package, assembled in this browser from the per-area packages the API
 * returned, so its `url` is a same-origin blob and never expires. `epic` is a PNG per area,
 * also rendered here. Neither needs `download` to survive a cross-origin redirect.
 */
export interface CreatedPackage {
  /** True once the file was written to the place the person chose. */
  readonly saved?: boolean;
  readonly kind: "x1" | "epic";
  readonly fileName: string;
  readonly url: string;
  /**
   * The UPN the API recorded in the package. Empty for `epic`: an image has no schema-40
   * `ReviewedBy` field to carry it, so the review is enforced without being recorded - which
   * is exactly what the desktop application did for both outputs.
   */
  readonly reviewedBy: string;
}

/** What a finished recognition run was run with, which is what says whether it is still good. */
export interface CompletedRun {
  readonly jobId: string;
  /** EPIC skips OCR and always reconstructs, so a DWG result is not an EPIC result. */
  readonly outputType: OutputType;
  readonly scale: number;
  readonly polygon: readonly Point[];
  readonly sourceDocumentType: string;
}

/** One floor on one page. */
export interface AreaDraft {
  readonly polygon: readonly Point[];
  readonly polygonClosed: boolean;
  readonly jobPhase: JobPhase;
  /** The submitted job while it is in flight, so a reload can rejoin it. */
  readonly jobId: string | null;
  readonly run: CompletedRun | null;
}

export const EMPTY_AREA: AreaDraft = { polygon: [], polygonClosed: false, jobPhase: "none", jobId: null, run: null };

/** What belongs to a page rather than to one of its floors. */
export interface PageSetup {
  readonly calibration: Calibration;
  readonly floorCount: number;
  /** This page is one piece of a floor split across PDF pages; that floor gets no keyplan. */
  readonly skipKeyplan: boolean;
}

export const DEFAULT_PAGE_SETUP: PageSetup = {
  calibration: DEFAULT_CALIBRATION,
  floorCount: 1,
  skipKeyplan: false,
};

export interface ConversionState {
  readonly outputType: OutputType;
  readonly details: SchoolDetails;
  /** The page on screen. */
  readonly pageNumber: number;
  /** The floor being drawn on that page, 0-based. */
  readonly areaIndex: number;
  /** Display only. The engine never sees it; rotating invalidates nothing. */
  readonly viewRotation: 0 | 90 | 180 | 270;
  readonly pages: Readonly<Record<number, PageSetup>>;
  readonly areas: Readonly<Record<string, AreaDraft>>;
  readonly packagePhase: PackagePhase;
  readonly createdPackages: readonly CreatedPackage[];
}

export const INITIAL_STATE: ConversionState = {
  outputType: "DwgFloorPlan",
  details: EMPTY_DETAILS,
  pageNumber: 1,
  areaIndex: 0,
  viewRotation: 0,
  pages: {},
  areas: {},
  packagePhase: "none",
  createdPackages: [],
};

/** How close, in preview pixels, a click has to land on the first vertex to close the area. */
export const CLOSE_RADIUS = 12;

/** The engine's minimum: an area needs three points, and two do not bound one. */
export const MIN_POLYGON_POINTS = 3;

export function areaKey(pageNumber: number, areaIndex: number): string {
  return `${pageNumber}:${areaIndex}`;
}

export function pageSetup(state: ConversionState, pageNumber: number): PageSetup {
  return state.pages[pageNumber] ?? DEFAULT_PAGE_SETUP;
}

export function areaDraft(state: ConversionState, pageNumber: number, areaIndex: number): AreaDraft {
  return state.areas[areaKey(pageNumber, areaIndex)] ?? EMPTY_AREA;
}

/** Inches per preview pixel, or null while the calibration is incomplete or unusable. */
export function scaleFor(calibration: Calibration): number | null {
  const { from, to, distanceText } = calibration;
  if (from === null || to === null) return null;
  const inches = parseArchitecturalDistanceInches(distanceText);
  if (inches === null) return null;
  return inchesPerPreviewPixel(from, to, inches);
}

export interface EffectiveScale {
  readonly scale: number;
  /** The page whose own calibration this is. Equal to the page asked about when it owns one. */
  readonly sourcePage: number;
}

/**
 * The scale a page converts at: its own, else the lowest-numbered page that has one.
 *
 * The desktop application's `ResolveEffectiveCalibrationSource`. Keeping the rule in one place
 * is what lets the live status, the recognition run and the package agree on which scale a
 * page used.
 */
export function effectiveScale(state: ConversionState, pageNumber: number): EffectiveScale | null {
  const own = scaleFor(pageSetup(state, pageNumber).calibration);
  if (own !== null) return { scale: own, sourcePage: pageNumber };

  const owners = Object.keys(state.pages)
    .map(Number)
    .sort((a, b) => a - b);
  for (const candidate of owners) {
    const scale = scaleFor(pageSetup(state, candidate).calibration);
    if (scale !== null) return { scale, sourcePage: candidate };
  }
  return null;
}

/** The floors of a page, in order. */
export function floorIndexes(state: ConversionState, pageNumber: number): readonly number[] {
  return Array.from({ length: pageSetup(state, pageNumber).floorCount }, (_, i) => i);
}

export interface AreaRef {
  readonly pageNumber: number;
  readonly areaIndex: number;
  readonly key: string;
}

/**
 * The areas that will be converted: every floor of every page that has had a polygon started.
 *
 * A page with only a scale is a scale source, not something to convert, so it does not demand
 * polygons. Once any floor on a page is started, all of that page's floors are required - the
 * desktop application refuses to export a half-drawn page.
 */
export function areasInPlay(state: ConversionState): readonly AreaRef[] {
  const pages = new Set<number>();
  for (const key of Object.keys(state.areas)) {
    const draft = state.areas[key];
    if (draft !== undefined && draft.polygon.length > 0) pages.add(Number(key.split(":")[0]));
  }
  return [...pages]
    .sort((a, b) => a - b)
    .flatMap((pageNumber) =>
      floorIndexes(state, pageNumber).map((areaIndex) => ({
        pageNumber,
        areaIndex,
        key: areaKey(pageNumber, areaIndex),
      })),
    );
}

function samePolygon(a: readonly Point[], b: readonly Point[]): boolean {
  return a.length === b.length && a.every((p, i) => p.x === b[i]?.x && p.y === b[i]?.y);
}

/**
 * Whether an area's finished run was made with what is on screen now.
 *
 * Staleness is derived rather than cleared: changing a scale, or redrawing a polygon, does not
 * have to remember to reset every area it affects, including the pages that borrow the scale.
 * A result that was produced at a different scale is not a result for this configuration.
 */
export function runIsCurrent(state: ConversionState, ref: AreaRef): boolean {
  const draft = areaDraft(state, ref.pageNumber, ref.areaIndex);
  const scale = effectiveScale(state, ref.pageNumber);
  return (
    draft.run !== null &&
    scale !== null &&
    draft.run.outputType === state.outputType &&
    draft.run.scale === scale.scale &&
    samePolygon(draft.run.polygon, draft.polygon)
  );
}

export type AreaStatus = "undrawn" | "drawing" | "ready" | "running" | "failed" | "stale" | "done";

export function areaStatus(state: ConversionState, ref: AreaRef): AreaStatus {
  const draft = areaDraft(state, ref.pageNumber, ref.areaIndex);
  if (draft.jobPhase === "submitting" || draft.jobPhase === "queued" || draft.jobPhase === "running") {
    return "running";
  }
  if (!draft.polygonClosed || draft.polygon.length < MIN_POLYGON_POINTS) {
    return draft.polygon.length === 0 ? "undrawn" : "drawing";
  }
  if (runIsCurrent(state, ref)) return "done";
  if (draft.run !== null) return "stale";
  return draft.jobPhase === "failed" ? "failed" : "ready";
}

export interface StepStatus {
  readonly complete: boolean;
  /** Shown under the step title. One line, and specific. */
  readonly summary: string;
}

export function formatPageRanges(pages: readonly number[]): string {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === (sorted[j] as number) + 1) j += 1;
    parts.push(j === i ? `${sorted[i]}` : `${sorted[i]}-${sorted[j]}`);
    i = j + 1;
  }
  return parts.join(", ");
}

/**
 * "Calibration: page 2 set; pages 3-5 use it too", plus the pages with no scale at all.
 *
 * Only pages in play are listed as borrowing or lacking a scale: pages nobody is converting
 * are not a problem worth a warning.
 */
export function calibrationSummary(state: ConversionState): string {
  const inPlay = [...new Set(areasInPlay(state).map((a) => a.pageNumber))];
  const owners = Object.keys(state.pages)
    .map(Number)
    .filter((n) => scaleFor(pageSetup(state, n).calibration) !== null)
    .sort((a, b) => a - b);
  if (owners.length === 0) return inPlay.length === 0 ? "No scale set yet" : `No scale: ${formatPageRanges(inPlay)}`;

  const parts = [`page${owners.length === 1 ? "" : "s"} ${formatPageRanges(owners)} set`];
  const borrowers = inPlay.filter((n) => !owners.includes(n));
  if (borrowers.length > 0) parts.push(`${formatPageRanges(borrowers)} use${borrowers.length === 1 ? "s" : ""} it too`);
  return parts.join("; ");
}

interface SourceSummary {
  readonly fileName: string;
  readonly pageCount: number;
  readonly preview: Size;
}

export function stepStatus(
  step: StepId,
  state: ConversionState,
  source: SourceSummary | null,
): StepStatus {
  switch (step) {
    case "output":
      return {
        complete: true,
        summary:
          state.outputType === "DwgFloorPlan"
            ? "X1 .DWG - walls, doors and Room blocks"
            : "EPIC Map (.PNG) - no OCR, no room tags",
      };

    case "source":
      return source === null
        ? { complete: false, summary: "No source selected" }
        : {
            complete: true,
            summary:
              source.pageCount > 1
                ? `${source.fileName} - ${source.pageCount} pages`
                : source.fileName,
          };

    case "details": {
      // Not required to *convert*. The engine never sees any of it - it is carried into the
      // package for the TLX title block - so blocking recognition on a Portal ID would stop
      // the work for the sake of paperwork. The school name is required to package, because
      // the output drawing is named from it, and step 8 says so rather than this step.
      const named = state.details.schoolName.trim() !== "";
      return {
        complete: named,
        summary: named ? state.details.schoolName.trim() : "Needed to name the drawing",
      };
    }

    case "preview":
      return source === null
        ? { complete: false, summary: "Open a file first" }
        : {
            complete: true,
            summary: source.pageCount > 1 ? `Page ${state.pageNumber} of ${source.pageCount}` : "Sheet loaded",
          };

    case "calibration": {
      const own = pageSetup(state, state.pageNumber).calibration;
      const scale = scaleFor(own);
      if (scale !== null) return { complete: true, summary: "Scale set" };

      if (own.from !== null && own.to !== null) {
        return { complete: false, summary: "That distance is not a measurement the parser recognises" };
      }
      const inherited = effectiveScale(state, state.pageNumber);
      if (inherited !== null) {
        return { complete: true, summary: `Uses page ${inherited.sourcePage}'s scale` };
      }
      return { complete: false, summary: "No calibration points on this page" };
    }

    case "area": {
      const inPlay = areasInPlay(state);
      if (inPlay.length === 0) {
        return { complete: false, summary: "No conversion area drawn yet" };
      }
      const drawn = inPlay.filter((ref) => {
        const status = areaStatus(state, ref);
        return status !== "undrawn" && status !== "drawing";
      });
      const pages = new Set(inPlay.map((a) => a.pageNumber)).size;
      return {
        complete: drawn.length === inPlay.length,
        summary: `${drawn.length} of ${inPlay.length} area${inPlay.length === 1 ? "" : "s"} drawn on ${pages} page${pages === 1 ? "" : "s"}`,
      };
    }

    case "recognition": {
      const inPlay = areasInPlay(state);
      if (inPlay.length === 0) return { complete: false, summary: "Recognition has not been run" };
      const statuses = inPlay.map((ref) => areaStatus(state, ref));
      if (statuses.includes("running")) return { complete: false, summary: "Recognising. This takes a few minutes." };
      if (statuses.includes("failed")) return { complete: false, summary: "Recognition failed for an area" };
      const done = statuses.filter((s) => s === "done").length;
      if (done === inPlay.length) return { complete: true, summary: "Recognition complete" };
      if (statuses.includes("stale")) {
        return { complete: false, summary: "A scale or area changed since recognition ran" };
      }
      return { complete: false, summary: `${done} of ${inPlay.length} areas recognised` };
    }

    case "output-package": {
      const noun = state.outputType === "EpicMap" ? "EPIC map" : "package";
      if (state.packagePhase === "creating") {
        return { complete: false, summary: `Creating the ${noun}...` };
      }
      const first = state.createdPackages[0];
      if (first !== undefined) {
        return {
          complete: true,
          summary:
            state.createdPackages.length === 1
              ? `${first.fileName} is ready`
              : `${state.createdPackages.length} EPIC maps are ready`,
        };
      }
      if (state.packagePhase === "failed") {
        return { complete: false, summary: `The ${noun} could not be created` };
      }
      if (!allAreasRecognised(state)) return { complete: false, summary: "Run recognition first" };
      return { complete: false, summary: `Ready to create the ${noun}` };
    }
  }
}

function allAreasRecognised(state: ConversionState): boolean {
  const inPlay = areasInPlay(state);
  return inPlay.length > 0 && inPlay.every((ref) => runIsCurrent(state, ref));
}

/**
 * The areas recognition would run now: drawn, scaled, and without a current result.
 *
 * Areas already current are not re-run, which is what makes a failed area on page 7 retryable
 * without paying again for the six that finished.
 */
export function areasToRecognise(state: ConversionState): readonly AreaRef[] {
  return areasInPlay(state).filter((ref) => {
    const status = areaStatus(state, ref);
    return (status === "ready" || status === "stale" || status === "failed") && effectiveScale(state, ref.pageNumber) !== null;
  });
}

/**
 * Whether recognition can be submitted, and if not, why.
 *
 * Returning the reason rather than a boolean is what lets the button say "Draw a conversion
 * area first" instead of sitting there greyed out with no explanation, which is the single
 * most common way a stepped form wastes somebody's afternoon.
 */
export function blockedReason(state: ConversionState, source: unknown | null): string | null {
  if (source === null) return "Open a floor-plan file first";

  const inPlay = areasInPlay(state);
  if (inPlay.length === 0) return "Draw and finish a conversion area first";

  for (const ref of inPlay) {
    const status = areaStatus(state, ref);
    if (status === "drawing" || status === "undrawn") {
      return `Finish area ${ref.areaIndex + 1} on page ${ref.pageNumber} - converting a whole page is not supported`;
    }
  }
  for (const ref of inPlay) {
    if (effectiveScale(state, ref.pageNumber) === null) {
      return `Set the scale for page ${ref.pageNumber} - the output has no real-world size without it`;
    }
  }
  if (inPlay.some((ref) => areaStatus(state, ref) === "running")) {
    return "Recognition is already running";
  }
  if (areasToRecognise(state).length === 0) return "Every area is already recognised";
  return null;
}

/** Steps done, for the progress indicator. The desktop app shows "Step N of 8". */
export function completedCount(state: ConversionState, source: SourceSummary | null): number {
  return STEPS.filter((step) => stepStatus(step.id, state, source).complete).length;
}

/**
 * Whether a package can be created, and if not, why.
 *
 * The same shape and the same reason as `blockedReason`: a greyed-out button with no
 * explanation is how a stepped form wastes somebody's afternoon.
 *
 * Clicking the create button is the operator's review confirmation, so there is no separate
 * gate for it here. The API still requires the flag on a raster page and the click sends it.
 */
export function packageBlockedReason(state: ConversionState): string | null {
  const epic = state.outputType === "EpicMap";
  const noun = epic ? "EPIC map" : "package";

  const inPlay = areasInPlay(state);
  if (inPlay.length === 0) return "Run recognition first";
  if (state.packagePhase === "creating") return `Already creating the ${noun}`;
  if (!allAreasRecognised(state)) {
    return inPlay.some((ref) => areaDraft(state, ref.pageNumber, ref.areaIndex).run !== null)
      ? "Some areas are out of date or not recognised yet - run recognition again"
      : "Run recognition first";
  }
  if (state.details.schoolName.trim() === "") {
    return epic
      ? "Enter the school name in step 3 - the image is named from it"
      : "Enter the school name in step 3 - the output drawing is named from it";
  }

  return null;
}

/** The lines of the desktop application's "Review settings" box, one per fact a drafter checks. */
export function reviewLines(state: ConversionState, source: { readonly fileName: string } | null): readonly string[] {
  const lines = [
    `Source: ${source?.fileName ?? "none"}`,
    `Output: ${state.outputType === "DwgFloorPlan" ? "X1 .DWG floor plan" : "EPIC map"}`,
    `School: ${state.details.schoolName.trim() === "" ? "(not entered)" : state.details.schoolName.trim()}`,
  ];
  const inPlay = areasInPlay(state);
  const pages = [...new Set(inPlay.map((a) => a.pageNumber))];
  for (const pageNumber of pages) {
    const scale = effectiveScale(state, pageNumber);
    const own = scale !== null && scale.sourcePage === pageNumber;
    const setup = pageSetup(state, pageNumber);
    const scaleText =
      scale === null
        ? "no scale"
        : own
          ? `own scale (${setup.calibration.distanceText})`
          : `uses page ${scale.sourcePage}'s scale`;
    const floors = `${setup.floorCount} floor${setup.floorCount === 1 ? "" : "s"}`;
    lines.push(`Page ${pageNumber}: ${scaleText}; ${floors}${setup.skipKeyplan ? "; split floor plan, no keyplan" : ""}`);
  }
  const defaultSource = effectiveScale(state, pages[0] ?? 1);
  if (defaultSource !== null) lines.push(`Default scale page: ${defaultSource.sourcePage}`);
  return lines;
}
